import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Star as StarIcon, GripVertical } from "lucide-react";

const LONG_PRESS_MS = 350;   // Time before drag engages (compromise between accidental drags and feeling slow)
const CANCEL_DIST_PX = 8;    // Movement before engage cancels the drag (interpreted as scroll)

/**
 * Horizontal thumbnail strip shown below the main photo carousel. Each
 * thumbnail is a clickable button that jumps the carousel to that slide;
 * the currently-active slide is outlined + slightly larger so it's
 * discoverable at a glance. Rendered only for scans with 2+ photos.
 *
 * Reorder — long-press any EXTRA thumbnail (index > 0, i.e. not the
 * primary cover) to engage drag mode. While engaged, the thumb
 * translates with the pointer + shows a "grip" affordance; on release
 * we commit the new order via `onReorderExtras(newIds)`. The primary
 * cover slot stays fixed — promoting requires the explicit
 * "Set as cover" action so accidental thumb-swipes never displace it.
 */
export const PhotoThumbStrip = ({
  photos,
  extras = [],
  activeIndex,
  onSelectIndex,
  onReorderExtras,
  altBase,
}) => {
  const { t } = useTranslation();
  const stripRef = useRef(null);
  const dragState = useRef(null); // { startI, currentI, timer, pointerId, started, startX, dx }
  const [renderTick, setRenderTick] = useState(0);
  const forceRender = () => setRenderTick((v) => v + 1);
  // Local extras order used while dragging so the "slot" the pointer
  // is currently over shifts visually as the user swipes across
  // neighbors. Committed to the parent via `onReorderExtras` on drop.
  const [liveExtras, setLiveExtras] = useState(null);

  if (!Array.isArray(photos) || photos.length < 2) return null;

  const canReorder = typeof onReorderExtras === "function" && extras.length > 1;

  // Compute current photos array (uses liveExtras during drag so
  // neighbors visually shift into place).
  const displayPhotos = liveExtras
    ? [photos[0], ...liveExtras.map((e) => e.image_base64)]
    : photos;

  // Find which slot the pointer is currently over. Iterates the strip's
  // child rects each move — O(N) but N is ≤ 4 (primary + 3 extras cap),
  // so it's negligible even on low-end phones. Skips the primary AND
  // the currently-dragged thumb — the latter is translated with the
  // pointer so its rect always contains the pointer, which would pin
  // `currentI` in place and prevent the swap from ever detecting a
  // neighbour crossing.
  const slotAtPointer = (clientX, draggedIdx) => {
    const el = stripRef.current;
    if (!el) return null;
    const children = el.querySelectorAll("[data-thumb-index]");
    for (let i = 0; i < children.length; i++) {
      const idx = Number(children[i].getAttribute("data-thumb-index"));
      if (idx === 0 || idx === draggedIdx) continue;
      const c = children[i].getBoundingClientRect();
      if (clientX >= c.left && clientX <= c.right) return idx;
    }
    return null;
  };

  const onPointerDown = (e, i) => {
    if (!canReorder || i === 0) return;
    // Capture the pointer immediately (not after the long-press timer)
    // so a fast flick that leaves the thumb in a single move still
    // routes pointermove/up back here and can cancel the timer.
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    const state = {
      startI: i,
      currentI: i,
      pointerId: e.pointerId,
      started: false,
      startX: e.clientX,
      dx: 0,
      timer: null,
    };
    state.timer = setTimeout(() => {
      // Engage drag mode if we haven't already cancelled via move.
      if (dragState.current && dragState.current.pointerId === state.pointerId) {
        dragState.current.started = true;
        // Measure the nominal slot pitch ONCE at engage time, before
        // any translate transforms are applied. Using the live rect of
        // the already-translated dragged thumb would produce an
        // overshoot equal to half the pitch (~28px in practice).
        const el = stripRef.current;
        if (el) {
          const first = el.querySelector('[data-thumb-index="1"]');
          const second = el.querySelector('[data-thumb-index="2"]');
          if (first && second) {
            const fr = first.getBoundingClientRect();
            const sr = second.getBoundingClientRect();
            dragState.current.slotPitch =
              (sr.left + sr.width / 2) - (fr.left + fr.width / 2);
          }
        }
        // Snapshot the current extras array so we can mutate liveExtras
        // as neighbors shift under the pointer.
        setLiveExtras(extras.slice());
        try { navigator.vibrate?.(20); } catch { /* Safari lacks .vibrate */ }
        forceRender();
      }
    }, LONG_PRESS_MS);
    dragState.current = state;
  };

  const onPointerMove = (e) => {
    const state = dragState.current;
    if (!state || state.pointerId !== e.pointerId) return;
    const dx = e.clientX - state.startX;
    if (!state.started) {
      // Pre-engage: any real movement cancels the long-press (user is
      // probably trying to scroll the strip).
      if (Math.abs(dx) > CANCEL_DIST_PX) {
        clearTimeout(state.timer);
        dragState.current = null;
      }
      return;
    }
    state.dx = dx;
    // Determine which extra-slot we're hovering; if we crossed a
    // neighbor's center, swap them in `liveExtras` so the visual
    // reorder is immediate.
    const target = slotAtPointer(e.clientX, state.currentI);
    if (target != null && target !== state.currentI) {
      // Capture indices BEFORE we mutate `state.currentI` — React 18
      // runs functional setState updaters lazily, so reading
      // `state.currentI` inside the updater would see the already-
      // advanced value and the splice would be a no-op.
      const fromExtraIdx = state.currentI - 1;
      const toExtraIdx = target - 1;
      // Rebase startX by the NOMINAL slot pitch measured once at
      // drag-engage time (see onPointerDown's timer). Using the live
      // rect of the transformed dragged thumb would carry the current
      // translateX(dx) into the delta and produce a residual overshoot.
      if (state.slotPitch) {
        // Sign matches direction: dragging right (target > currentI)
        // pushes startX right by +pitch; left drag by -pitch. Multi-
        // slot jumps scale linearly.
        state.startX += state.slotPitch * (target - state.currentI);
      }
      state.currentI = target;
      state.dx = e.clientX - state.startX;
      setLiveExtras((prev) => {
        if (!prev) return prev;
        if (
          fromExtraIdx < 0 || toExtraIdx < 0 ||
          fromExtraIdx >= prev.length || toExtraIdx >= prev.length
        ) return prev;
        const next = prev.slice();
        const [moved] = next.splice(fromExtraIdx, 1);
        next.splice(toExtraIdx, 0, moved);
        return next;
      });
    }
    forceRender();
  };

  const onPointerUp = (e) => {
    const state = dragState.current;
    if (!state || state.pointerId !== e.pointerId) return;
    clearTimeout(state.timer);
    const wasStarted = state.started;
    const startI = state.startI;
    const currentI = state.currentI;
    dragState.current = null;
    if (!wasStarted) return; // click passes through to the button's onClick
    // Commit the reorder to the parent if it actually moved.
    if (currentI !== startI && liveExtras) {
      const ids = liveExtras.map((x) => x.photo_id).filter(Boolean);
      onReorderExtras?.(ids);
    }
    // Clear the local override — parent state will drive the next render.
    setLiveExtras(null);
    forceRender();
  };

  return (
    <div
      ref={stripRef}
      className="mx-5 mt-3 flex items-center gap-2 overflow-x-auto no-scrollbar animate-fade-up"
      data-testid="photo-thumb-strip"
      role="tablist"
      aria-label={t("result.thumbStripAria")}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: dragState.current?.started ? "none" : "pan-x" }}
    >
      {displayPhotos.map((src, i) => {
        // Prefer photo_id as the React key so a mid-drag swap does NOT
        // unmount the pressed button — otherwise the setPointerCapture
        // is silently dropped and pointerup never routes back here.
        const stableId = i === 0
          ? "primary"
          : (liveExtras
              ? liveExtras[i - 1]?.photo_id
              : extras[i - 1]?.photo_id) || `i-${i}`;
        const isActive = i === activeIndex;
        const state = dragState.current;
        const isDragging = state?.started && state?.currentI === i;
        const thumbStyle = isDragging
          ? { transform: `translateX(${state.dx}px) scale(1.08)`, zIndex: 10, transition: "none" }
          : undefined;
        return (
          <button
            key={stableId}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={t("result.thumbNavAria", { n: i + 1, total: displayPhotos.length })}
            data-testid={`photo-thumb-${i}`}
            data-thumb-index={i}
            data-active={isActive ? "true" : undefined}
            data-dragging={isDragging ? "true" : undefined}
            onClick={() => {
              // Suppress the click that follows a real drag so the
              // carousel doesn't accidentally jump to the drop slot.
              if (state?.started) return;
              onSelectIndex(i);
            }}
            onPointerDown={(e) => onPointerDown(e, i)}
            style={thumbStyle}
            className={
              "relative shrink-0 rounded-lg overflow-hidden transition-all focus:outline-none focus:ring-2 focus:ring-leaf/60 " +
              (isActive
                ? "w-14 h-14 ring-2 ring-leaf ring-offset-1 ring-offset-parchment shadow-botanical"
                : "w-12 h-12 opacity-70 hover:opacity-100") +
              (isDragging ? " ring-2 ring-leaf shadow-2xl" : "")
            }
          >
            <img
              src={src}
              alt={`${altBase || ""} — ${i + 1}`}
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
              loading="lazy"
              decoding="async"
            />
            {i === 0 && (
              <span
                aria-hidden="true"
                className="absolute bottom-0.5 left-0.5 rounded-full bg-black/55 backdrop-blur w-3.5 h-3.5 flex items-center justify-center text-white"
              >
                <StarIcon className="w-2 h-2" strokeWidth={2} fill="currentColor" />
              </span>
            )}
            {isDragging && (
              // Grip icon that only appears while the thumb is actively
              // being dragged — confirms drag mode has engaged.
              <span
                aria-hidden="true"
                className="absolute top-0.5 right-0.5 rounded-full bg-black/55 backdrop-blur w-4 h-4 flex items-center justify-center text-white"
              >
                <GripVertical className="w-2.5 h-2.5" strokeWidth={2} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PhotoThumbStrip;
